import React from "react";
import { Skeleton } from "@vibe/core";

/**
 * A page's worth of placeholder rows, for the loading state of either tab's parent list.
 *
 * Shared rather than copied because the two lists sit one tab click apart and are meant to
 * read as one product: the Items tab has had this loading state since Phase 1, while the
 * Subitems tab had none at all — during its scan it fell through to its list render with zero
 * rows, so a board mid-scan showed an empty bordered box that is indistinguishable from a
 * board with nothing in it.
 *
 * `rows` is the caller's page size, never a literal. The Items tab's version stood at a
 * hardcoded 8 for a while, which agreed with the page only as long as the page happened to
 * hold 8; at 25 the loading state collapsed to a third of the height it was standing in for
 * and the list jumped when the rows landed.
 */
const DiscrepancyListSkeleton: React.FC<{ rows: number }> = ({ rows }) => (
  <>
    {Array.from({ length: rows }).map((_, index) => (
      <Skeleton
        key={index}
        fullWidth={true}
        wrapperClassName="w-full rounded-xl"
        // One height for both lists. Their real rows differ by a few pixels (Items pads a
        // 40px minimum with py-2; a Subitems parent row pads a 40px minimum with
        // --spacing-small), and matching each list exactly would mean two numbers to keep
        // in step for a difference no one can see. What matters is that neither is
        // standing in for a row of a wildly different height, which is what causes the jump.
        height={60}
        className="w-full"
      />
    ))}
  </>
);

export default DiscrepancyListSkeleton;
